import { IconClock, IconPen } from "@tine/designsystem-icons/sharp";
import { Group, Stack } from "@tine/designsystem-ui-react";
import isNil from "lodash-es/isNil";
import { observer } from "mobx-react-lite";
import { useLocation, useSearchParams } from "wouter";

import { formatDate, formatDateTime, parseM3Date } from "../common/utils/dateUtils";
import Button from "../components/Button";
import InfoLine from "../components/InfoLine";
import { InternalLink } from "../components/Links";
import cartStore from "../stores/cart/cartStore";

const SubscriptionEditingDeliveryInfo = observer(() => {
   const [_, redirectTo] = useLocation();
   if (isNil(cartStore.editing)) {
      return null;
   }

   const label = cartStore.editing.instanceDate
      ? `planlagt levering (${formatDate(parseM3Date(cartStore.editing.instanceDate))}), abo. `
      : "abonnement";
   const { orderNumber } = cartStore.editing;
   const redirectUrl = cartStore.editing.deadline ? `/customer/orders/${orderNumber}` : `/customer/subscriptions/${orderNumber}`;
   return (
      <div className="flex flex-wrap items-center justify-between w-full gap-4 lg:flex-row">
         <InternalLink href="/checkout/cart">
            Endrer {label} #{orderNumber}
         </InternalLink>
         <Button
            onClick={() => {
               cartStore.stopEditOrderMode().then(() => redirectTo(redirectUrl));
            }}
         >
            Avbryt endring
         </Button>

         <p className="m-0 text-sm">
            Endringene kan gjøres direkte i handlekurven. <br /> Oppdater abonnement for å lagre endringer.
         </p>
      </div>
   );
});

const DefaultEditingDeliveryInfo = observer(() => {
   const [_, redirectTo] = useLocation();
   const [searchParams] = useSearchParams();
   const hasConfirmationContext = searchParams.get("context") === "confirmation";
   if (isNil(cartStore.editing) || hasConfirmationContext) {
      return null;
   }

   const { orderNumber } = cartStore.editing;

   return (
      <>
         <div className="flex flex-wrap justify-between w-full gap-4 lg:flex-row">
            <div className="flex items-center">
               <IconClock />
               <InfoLine
                  rowOnMobile
                  label="Endringsfrist"
                  align="start"
                  value={formatDateTime(cartStore.editing.deadline, true, true)}
               />
            </div>

            <Button
               onClick={() => {
                  cartStore.stopEditOrderMode().then(() => redirectTo(`/customer/orders/${orderNumber}`));
               }}
            >
               Avbryt endring
            </Button>

            <Button iconPosition="left" icon={<IconPen />} to="/checkout/cart" variant="tertiary">
               Endrer ordre #{cartStore.editing.orderNumber}
            </Button>
         </div>
         <div className="flex">
            <p className="m-0 text-sm">
               Endringene kan gjøres direkte i handlekurven. Gå til kassen og send inn ordren for å lagre endringene.
            </p>
         </div>
      </>
   );
});

// Separate top bar when we are editing an order. TypeScript is a silly goose and doesn't understand that the existing check
// ensures the same thing as this added section section. To keep it easily humanly readable I am leaving both.
const ChangeOrderOrSubscription = () => {
   if (cartStore.isEditing() && !isNil(cartStore.editing)) {
      return (
         <Group justify="center" className="shadow-md bg-surface-default">
            <Stack className="w-full max-w-[--main-column-max-width] px-3 py-6 md:px-12">
               {cartStore.isEditingSubscription() ? <SubscriptionEditingDeliveryInfo /> : <DefaultEditingDeliveryInfo />}
            </Stack>
         </Group>
      );
   }
   return null;
};

export default observer(ChangeOrderOrSubscription);
